import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Dashboard from "./pages/Dashboard";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import AdminPanel from "./pages/AdminPanel";
import UserSettings from "./pages/UserSettings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import Contact from "./pages/Contact";
import Subscription from "./pages/Subscription";
import Unsubscribe from "./pages/Unsubscribe";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import MarineForecastPage from "./pages/MarineForecastPage";
import HuntingScoutingPage from "./pages/HuntingScoutingPage";
import SkiSnowReportPage from "./pages/SkiSnowReportPage";
import LakePage from "./pages/LakePage";
import MyLandPage from "./pages/MyLandPage";
import FishingConditionsPage from "./pages/FishingConditionsPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-current/20 border-t-current rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const HomeRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="home-loading-state">
        <div className="w-8 h-8 border-2 border-current/20 border-t-current rounded-full animate-spin" />
      </div>
    );
  }
  return user ? <Dashboard /> : <LandingPage />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-current/20 border-t-current rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Separate component to use theme hook
const AppContent = () => {
  const { theme } = useTheme();
  
  return (
    <div className="App min-h-screen transition-colors duration-300">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route 
            path="/" 
            element={<HomeRoute />} 
          />
          <Route path="/landing" element={<LandingPage />} />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <UserSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            } 
          />
          {/* Public Policy Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/pricing" element={<Subscription />} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route 
            path="/marine" 
            element={
              <ProtectedRoute>
                <MarineForecastPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hunting" 
            element={
              <ProtectedRoute>
                <HuntingScoutingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ski" 
            element={
              <ProtectedRoute>
                <SkiSnowReportPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/lake" 
            element={
              <ProtectedRoute>
                <LakePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fishing" 
            element={
              <ProtectedRoute>
                <FishingConditionsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-land" 
            element={
              <ProtectedRoute>
                <MyLandPage />
              </ProtectedRoute>
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" theme={theme} />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
